'use client';

import { useRouter } from 'next/navigation';
import { degerFormatlaBirim } from '@/lib/urunGrubuTipleri';

interface Ozellik {
  id: number;
  kod: string;
  etiket: string;
  tip: string;
  birim: string | null;
  varyasyon_secici: boolean;
}

interface GrupUrun {
  id: number;
  urun_slug: string | null;
  ozellik_degerleri: { ozellik_id: number; deger: string }[];
}

interface Props {
  ozellikler: Ozellik[];
  ozellikSecenekleri: Record<string, string[]>;
  urunler: GrupUrun[];
  aktifUrunSlug: string;
}

export default function UrunGrubuSecici({
  ozellikler, ozellikSecenekleri, urunler, aktifUrunSlug,
}: Props) {
  const router = useRouter();

  const varyasyonOzellikler = ozellikler.filter(o => o.varyasyon_secici);
  if (varyasyonOzellikler.length === 0) return null;

  // Aktif ürünün seçili değerleri
  const aktifUrun = urunler.find(u => u.urun_slug === aktifUrunSlug);
  const seciliDegerler: Record<string, string> = {};
  for (const o of varyasyonOzellikler) {
    const d = aktifUrun?.ozellik_degerleri.find(d => d.ozellik_id === o.id);
    if (d) seciliDegerler[o.kod] = d.deger;
  }

  const handleSecim = (ozellikKod: string, yeniDeger: string) => {
    const yeniSecimler = { ...seciliDegerler, [ozellikKod]: yeniDeger };

    // Tam eşleşen ürünü bul
    const eslesen = urunler.find(urun =>
      varyasyonOzellikler.every(o => {
        const hedef = yeniSecimler[o.kod];
        if (!hedef) return true;
        const urunDeger = urun.ozellik_degerleri.find(d => d.ozellik_id === o.id);
        return urunDeger?.deger === hedef;
      })
    );

    if (eslesen?.urun_slug && eslesen.urun_slug !== aktifUrunSlug) {
      router.push(`/${eslesen.urun_slug}`);
    }
  };

  return (
    <div className="space-y-5">
      {varyasyonOzellikler.map(ozellik => {
        const secenekler = ozellikSecenekleri[ozellik.kod] ?? [];
        const seciliDeger = seciliDegerler[ozellik.kod];

        return (
          <div key={ozellik.id}>
            <p className="text-sm font-semibold text-[var(--color-gray-700)] mb-2">
              {ozellik.etiket}
              {seciliDeger && (
                <span className="ml-2 font-normal text-[var(--color-primary)]">
                  {degerFormatlaBirim(seciliDeger, ozellik.birim)}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {secenekler.map(secenek => {
                const aktif = seciliDeger === secenek;

                // Bu seçenek + mevcut diğer seçimlerle eşleşen ürün var mı?
                const yeniSecimler = { ...seciliDegerler, [ozellik.kod]: secenek };
                const mevcut = urunler.some(urun =>
                  varyasyonOzellikler.every(o => {
                    const hedef = yeniSecimler[o.kod];
                    if (!hedef) return true;
                    const d = urun.ozellik_degerleri.find(d => d.ozellik_id === o.id);
                    return d?.deger === hedef;
                  })
                );

                return (
                  <button
                    key={secenek}
                    onClick={() => handleSecim(ozellik.kod, secenek)}
                    disabled={!mevcut}
                    className={`px-4 py-2 rounded-[var(--radius-lg)] border text-sm font-medium transition ${
                      aktif
                        ? 'border-[var(--color-primary)] bg-[var(--color-info-light)] text-[var(--color-primary)]'
                        : mevcut
                        ? 'border-[var(--color-gray-300)] hover:border-[var(--color-primary)] text-[var(--color-gray-700)]'
                        : 'border-[var(--color-gray-200)] text-[var(--color-gray-300)] cursor-not-allowed line-through'
                    }`}
                  >
                    {degerFormatlaBirim(secenek, ozellik.birim)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}